// contexts/AuthContext.jsx
import React, { useState, useEffect, createContext, useContext } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve essere usato dentro AuthProvider');
  return ctx;
};

//const API_BASE = 'http://localhost:5000/api';
const API_BASE = import.meta.env.VITE_API_BASE || 'https://meeting.aiav.eu/api';
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token') || null);

  // LOGIN
  const login = async (email, password) => {
    try {
      const r = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      const data = await r.json();
      if (data?.success) {
        const accessToken = data.data.accessToken;
        setToken(accessToken);
        setUser(data.data.user);
        localStorage.setItem('token', accessToken);
        return { success: true };
      }
      return { success: false, message: data?.message || 'Login fallito' };
    } catch {
      return { success: false, message: 'Errore di connessione' };
    }
  };

  // REGISTER
  const register = async (userData) => {
    try {
      const r = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(userData)
      });
      const data = await r.json();
      if (data?.success) {
        const accessToken = data.data.accessToken;
        setToken(accessToken);
        setUser(data.data.user);
        localStorage.setItem('token', accessToken);
        return { success: true };
      }
      return { success: false, message: data?.message || 'Registrazione fallita', errors: data?.errors };
    } catch {
      return { success: false, message: 'Errore di connessione' };
    }
  };

  // FORGOT PASSWORD
  const forgotPassword = async (email) => {
    try {
      const r = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await r.json();
      return { success: data?.success || false, message: data?.message || 'Errore' };
    } catch {
      return { success: false, message: 'Errore di connessione' };
    }
  };

  // RESET PASSWORD
  const resetPassword = async (token, password) => {
    try {
      const r = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      const data = await r.json();
      return { success: data?.success || false, message: data?.message || 'Errore' };
    } catch {
      return { success: false, message: 'Errore di connessione' };
    }
  };

  // VERIFY EMAIL
  const verifyEmail = async (token) => {
    try {
      const r = await fetch(`${API_BASE}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      const data = await r.json();
      return { success: data?.success || false, message: data?.message || 'Errore' };
    } catch {
      return { success: false, message: 'Errore di connessione' };
    }
  };

  // Refresh token
  const refreshAccessToken = async () => {
    try {
      const r = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await r.json();
      if (data?.success) {
        const accessToken = data.data.accessToken;
        setToken(accessToken);
        localStorage.setItem('token', accessToken);
        return accessToken;
      }
    } catch {}
    return null;
  };

  // LOGOUT
  const logout = () => {
    fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    }).catch(() => {});
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  // Verifica token all'avvio
  useEffect(() => {
    const verify = async () => {
      if (!token) { setLoading(false); return; }
      try {
        const r = await fetch(`${API_BASE}/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (r.status === 401) {
          const newTok = await refreshAccessToken();
          if (newTok) {
            const r2 = await fetch(`${API_BASE}/auth/verify`, {
              headers: { Authorization: `Bearer ${newTok}` }
            });
            const d2 = await r2.json();
            if (d2?.success) {
              setUser(d2.data.user);
              setLoading(false);
              return;
            }
          }
          logout();
        } else {
          const data = await r.json();
          if (data?.success) setUser(data.data.user);
          else logout();
        }
      } catch {
        logout();
      } finally {
        setLoading(false);
      }
    };
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <AuthContext.Provider value={{
      user, token, login, register, logout, loading,
      forgotPassword, resetPassword, verifyEmail,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin',
      isCollaborator: user?.role === 'collaborator',
      isUser: user?.role === 'user'
    }}>
      {children}
    </AuthContext.Provider>
  );
};